import * as React from 'react';
import { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { DataGrid, GridColDef, GridRowsProp, GridValidRowModel, GridFilterModel, GridToolbarContainer, GridToolbarFilterButton, getGridStringOperators, GridSortModel, getGridDateOperators } from '@mui/x-data-grid';
import useCustomFetch from '../hooks/useCustomFetch';
import AuthContext from '../context/AuthContext';
import Config from '../Config';
import { Box, Button, Chip, CircularProgress } from '@mui/material';
import { FiDownload, FiEye } from 'react-icons/fi';
import DialogDetailTransaction from './dialogs/DialogDetailTransaction'; // #POW-9
import { CheckInData, ShippingData } from '../types/Types'; // #POW-13
import { useTranslation } from "react-i18next"; // #POW-29
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'; // #POW-15

function formattedDate(date: string | Date) { //  #POW-8
  return new Intl.DateTimeFormat('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(date));
};

/**
 * #POW-11
 * Custom toolbar definition
 * @returns 
 */
function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

interface LogisticTransactionsChekedInDataGridProps {
  onFilterModelChange: (model: GridFilterModel) => void;
  onSelectedRowsChange: (rows: any[]) => void; // #POW-39
} // #POW-30

// #POW-20 
export default function LogisticTransactionsChekedInDataGrid({onFilterModelChange,onSelectedRowsChange}: LogisticTransactionsChekedInDataGridProps) {
  const { t } = useTranslation();  // #POW-29

  const customFetch = useCustomFetch()
  const auth = React.useContext(AuthContext)
  const { isReady } = useContext(AuthContext)
  const [isLoading, setIsLoading] = useState(false)

  /**
   * ---- Datasource, Rows and Columns definition 
   */
  const [selectedRows, setSelectedRows] = useState<GridValidRowModel[]>([]); // #POW-6
  const [shippingData, setShippingData] = useState<ShippingData>(); // #POW-13
  const [checkInData, setCheckInData] = useState<CheckInData>(); // #POW-21

  const [rows, setRows] = useState<GridRowsProp>([])
  const [rowCount, setRowCount] = useState(0);

  // pagination #POW-11
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20, });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'logistic_check_in_at',
      sort: 'desc',
    },
  ]);

  // filters #POW-11
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const memoizedFilters = useMemo(() => filterModel, [filterModel]);

  useEffect(() => {  // #POW-39
    onSelectedRowsChange(selectedRows); // call callback function to send data to the parent component
  }, [selectedRows]);
  
  /**
   * #POW-11
   * Enter Key press on filter input
   * @param event 
   */
  const handleFilterKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const newFilterModel = { ...filterModel };
      setFilterModel(newFilterModel);
      fetchData()
    }
  };

  /**
   * #POW-11
   * Handles filter model changes, reset included
   * @param model 
   */
  const setFilterModelChange =  (model: GridFilterModel) => {
    setFilterModel(model)
    if (model.items.length === 0) {
      setPaginationModel({page:0, pageSize:paginationModel.pageSize})
      fetchData(true)
    }
    onFilterModelChange(model) // #POW-30
  }

  // let's keep on backend supported filters #POW-11
  const supportedFilters = getGridStringOperators().filter((operator) => operator.value === 'contains');

  const supportedDateFilters = getGridDateOperators().filter((operator) => (operator.value === 'is')); // #POW-36

  // #POW-8 column definition
  const columns: GridColDef[] = [
    {
      field: 'id', headerName: t('tansactionTable.transaction'), minWidth: 150,
      filterOperators: supportedFilters
    },
    { field: 'date', headerName: t('tansactionTable.date'), minWidth: 180, 
      type: 'dateTime', // #POW-36
      valueGetter: (value) => value && new Date(value), // #POW-36
      filterOperators: supportedDateFilters // #POW-36
     },
    { field: 'logistic_custom_sku_transfer', headerName: t('tansactionTable.skuTransfer'), minWidth: 120 }, // #POW-18
    { field: 'logistic_check_in_at', headerName: t('tansactionTable.checkInDate'), minWidth: 180, 
      type: 'dateTime', // #POW-36
      valueGetter: (value) => value && new Date(value), // #POW-36
      filterOperators: supportedDateFilters // #POW-36
     }, // #POW-21
    { field: 'shop_code', headerName: t('tansactionTable.shopCode'), minWidth: 100 }, // #POW-20
    { field: 'shop_name', headerName: t('tansactionTable.shopName'), minWidth: 200 }, // #POW-20
    { field: 'logistic_custom_shipping_code', headerName: t('tansactionTable.shippingCode'), minWidth: 150 }, // #POW-20
    { field: 'logistic_shipping_date', headerName: t('tansactionTable.sentToLogisticAt'), minWidth: 180, 
      type: 'dateTime', // #POW-36
      valueGetter: (value) => value && new Date(value), // #POW-36
      filterOperators: supportedDateFilters // #POW-36
     }, // #POW-20
    {
      field: 'type', headerName: t('tansactionTable.type'), minWidth: 130,
      filterOperators: supportedFilters
    },
    {
      field: 'device_name', headerName: t('tansactionTable.device'), flex: 1.5, minWidth: 200,
      filterOperators: supportedFilters
    },
    {
      field: 'device_imei', headerName: t('tansactionTable.imei'), minWidth: 150,
      filterOperators: supportedFilters
    },
    {
      field: 'device_serial', headerName: t('tansactionTable.serialNumber'), minWidth: 200,
      filterOperators: supportedFilters
    },
    {
      field: 'actions', headerName: '', width: 200, filterable: false, sortable: false, renderCell: (params) => (
        <>
          <Button
            onClick={() => {
              handleClickOpen(params.row);
            }}
            variant="text"
          >
            <FiEye />
          </Button>
          <Button
            onClick={() => {
              handleDownload(params.row.id);
            }}
            variant="text"
          >
            <PictureAsPdfIcon />
          </Button>
        </>
      ),
    },
  ];

  /**
   * Row selection and Modal open/close
   */
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<{
    id: string,
    number: string,
    type: string,
    date: Date,
    completed: string,
    workflowStep: string,
    company: string,
    companyItn: string,
    status: string,
    printFormURL: string,
  } | null>(null);

  const handleClickOpen = (row: any) => {
    setSelectedRow(row);
    setShippingData({shippingCode: row.logistic_custom_shipping_code, shippingDate: row.logistic_shipping_date });
    setCheckInData({checkInDate: row.logistic_check_in_at}); // #POW-21
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // #POW-6 function called when selecting rows in the table
  const setRowSelectionModel = (newRowSelectionModel: any) => {
    // Map the selected ids to the corresponding row data
    const selectedRowsData = rows.filter((row) =>
      newRowSelectionModel.includes(row.id)
    );
    setSelectedRows(selectedRowsData);
  };

  /**
   * #POW-10
   * Handles the download of a transaction PDF.
   *
   * This function fetches a PDF file for a given transaction number from the server,
   * creates a temporary link element, and triggers a download of the PDF file.
   *
   * @param {string} transactionNumber - The unique identifier of the transaction to download.
   * @returns {Promise<void>} A promise that resolves when the download is complete.
   *
   * @throws Will log an error to the console if the download fails.
   */
  const handleDownload = async (transactionNumber: string) => {
    const url = `${Config.serverUrl}/api/trade-in/transaction/${transactionNumber}/pdf`;
    const response = await customFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/pdf',
      },
    });

    if (response.ok) {
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `transaction_${transactionNumber}.pdf`;
      link.click();
    } else {
      console.error('Failed to download PDF');
    }
  };

  /**
   * ---- Fetch data #POW-11
   */
  const fetchData = async (skipFilters=false) => {
    setIsLoading(true)
    const url = new URL(`${Config.serverUrl}/api/logistic/checked-in-transactions`);

    // #POW-11 ALWAYS present parametes
    url.searchParams.append('page', '' + paginationModel.page)
    url.searchParams.append('page_size', '' + paginationModel.pageSize)

    // #POW-11 Sorting
    if (sortModel.length > 0) {
      url.searchParams.append('sort_field', ''+sortModel[0].field)
      url.searchParams.append('sort_direction', ''+sortModel[0].sort)
    }

    // #POW-11 Filters
    if (!skipFilters) {
      const filterParams = filterModel.items.reduce((acc: any, filter: any) => {
        acc[filter.field] = filter.value;
        return acc;
      }, {});
      Object.keys(filterParams).forEach((key) => {
        url.searchParams.append(key, filterParams[key]);
      });
    }
    const savedFilters = { ...filterModel };


    // < the call >
    const response = await customFetch(url.toString(),
      {
        method: "GET",
        headers: {},
      },
    )
    const json = await response.json()
    setRows([])
    setRowCount(0)
    if (json && json.transactions) {
      setRows(
        json.transactions.map((item: any) => ({
          id: item.number, // map Picea "number" as "id"
          date: item.transaction_date,
          logistic_custom_sku_transfer: item.logistic_custom_sku_transfer, // #POW-18
          logistic_check_in_at: item.logistic_check_in_at, // #POW-21
          type: item.type,
          device_name: item.device_name,
          device_imei: item.device_imei,
          device_serial: item.device_sn,
          shop_code: item.shop_code, // #POW-20
          shop_name: item.shop_name, // #POW-20
          logistic_custom_shipping_code: item.logistic_custom_shipping_code, // #POW-20
          logistic_shipping_date: item.logistic_shipping_date, // #POW-20
        }))
      )
      setRowCount(json.totalCount)
    }
    if (!skipFilters) {
      setFilterModel(savedFilters);
    }
    setIsLoading(false)
  }


  React.useEffect(() => {
    if (isReady) {
      fetchData()
    }
  }, [isReady, paginationModel, sortModel]);

  return (
    <>
      <Box sx={{ width: '100%', maxWidth: { sm: '100%' }, height: '100%' }}>
        <DataGrid
          autoHeight
          loading={isLoading}
          checkboxSelection // #POW-39
          disableRowSelectionOnClick
          rows={rows}
          rowCount={rowCount}
          columns={columns}
          density="compact"

          // ------------ #POW-11 filters ------------
          filterMode="server"
          onFilterModelChange={setFilterModelChange}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }

          // ------------ #POW-11 pagination & sorting ------------ 
          paginationMode="server"
          initialState={{
            filter: { filterModel: filterModel }
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}

          sortingOrder={['desc', 'asc']} // #POW-41
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          
          // ------------ #POW-11 Toolbar and filters configuration  ------------ 
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                },
                columnInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                operatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: 'outlined',
                    size: 'small',
                    onKeyPress: handleFilterKeyPress,
                  },
                },
              },
            },
          }}
          // ------------ Row selection  ------------ 
          onRowSelectionModelChange={(newRowSelectionModel) => {  // #POW-39
            setRowSelectionModel(newRowSelectionModel);
          }}
        />
      </Box>

      <DialogDetailTransaction 
        open={open} 
        onClose={handleClose} 
        transactionNumber={selectedRow?.id} 
        shippingData={shippingData} 
        checkInData={checkInData} /> {/* #POW-21 */}
    </>
  );
}
